'use client';

import { useEffect, useState, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';


interface SectionRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

function SectionReveal({ children, className = '', delay = 0 }: SectionRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>

      {children}
    </div>);

}

interface PhotoPlaceholderProps {
  label: string;
  aspectClass?: string;
}

function PhotoPlaceholder({ label, aspectClass = 'aspect-[4/3]' }: PhotoPlaceholderProps) {
  return (
    <div className={`${aspectClass} rounded-2xl bg-gradient-to-br from-secondary to-muted border border-border flex flex-col items-center justify-center gap-3 overflow-hidden`}>
      <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
        <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
      <p className="text-xs text-muted-foreground tracking-wide text-center px-4">{label}</p>
    </div>);

}

export default function EducationImpactPage() {
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const honors = [
  'Ronald E. McNair Scholar (PhD Bound Undergrad to Grad Program)',
  'Sphinx Head Senior Honor Society',
  'Clerk of the House of Representatives International Women\'s Day Recognition',
  'Endocrine Society Award Winner',
  'Society of Women Engineers (SWE)',
  'National Society of Black Engineers (NSBE)',
  'American Society of Plant Biologists'];


  const healthcareItems = [
  'Affordable healthcare solutions',
  'Women\'s health innovation',
  'Cancer therapeutics',
  'Preventative health initiatives',
  'Medical technology development',
  'Expanding access to care'];


  const researchInstitutions = [
  {
    id: 'harvard',
    name: 'Harvard Medical School & Dana-Farber Cancer Institute',
    description: 'Conducted cancer research focused on developing innovative therapeutic approaches designed to improve treatment outcomes and expand possibilities for patients facing complex diseases.',
    label: 'Mary Bellakbira standing in front of Harvard Medical School',
    image: '/assets/images/IMG_8099-1781097341042.jpg'
  },
  {
    id: 'stanford',
    name: 'Stanford University',
    description: 'Explored entrepreneurship, emerging technologies, and innovation strategies that shape the future of healthcare and scientific discovery.',
    label: 'Stanford University campus and innovation hub'
  },
  {
    id: 'nasa',
    name: 'NASA',
    description: 'Researched plant biotechnology and space agriculture, exploring how plants can support future space missions while advancing sustainable food production both on Earth and beyond.',
    label: 'NASA research facility and space agriculture lab'
  }];


  return (
    <>
      <Header />
      <main className="bg-background">

        {/* ── Hero ── */}
        <section className="relative min-h-[70vh] flex items-end pt-40 pb-20 px-6 overflow-hidden bg-foreground">
          {/* Subtle grain texture overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />

          {/* Gold accent line */}
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" />

          <div className="relative z-10 max-w-7xl mx-auto w-full">
            <div className={`transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <p className="text-micro text-accent tracking-[0.25em] mb-6">Education & Impact</p>
              <h1 className="font-display text-6xl md:text-8xl font-light text-white mb-6 tracking-tight leading-none italic">
                Education changed<br />
                <span className="not-italic font-semibold">the trajectory</span>
              </h1>
              <div className="w-20 h-px bg-accent/60 mb-8" />
              <p className="text-lg md:text-xl text-white/70 font-light max-w-3xl leading-relaxed mb-10">
                As a first-generation college student, I quickly learned that education is about far more than earning a degree. It is a pathway to opportunity, a catalyst for innovation, and one of the most powerful tools we have to create meaningful change.
              </p>
              <p className="text-base text-white/50 font-light max-w-2xl leading-relaxed mb-10">
                Through science, research, entrepreneurship, and service, I have dedicated my academic journey to improving health outcomes, expanding access to opportunity, and empowering future generations to pursue their own dreams.
              </p>
              {/* Quote */}
              <div className="border-l-2 border-accent pl-6 max-w-2xl">
                <p className="text-accent/90 italic text-lg font-light leading-relaxed">
                  "Education opens doors. Impact is what we choose to do once we walk through them."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Academic Excellence ── */}
        <section className="py-28 px-6 bg-background">
          <div className="max-w-7xl mx-auto">
            <SectionReveal>
              <div className="flex items-center gap-4 mb-16">
                <span className="text-3xl">🎓</span>
                <div>
                  <p className="text-micro text-accent tracking-[0.2em] mb-1">Academic Excellence</p>
                  <h2 className="font-display text-4xl md:text-5xl font-light text-foreground italic">Cornell University</h2>
                </div>
              </div>
            </SectionReveal>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <SectionReveal delay={100}>
                <div>
                  <div className="mb-8">
                    <h3 className="text-2xl font-semibold text-foreground mb-2">Bachelor of Science </h3>
                    <p className="text-accent font-medium tracking-wide">In Biotechnology, Biomedical Engineering & Business</p>
                  </div>
                  <p className="text-foreground/70 leading-relaxed mb-10">
                    As a student at Cornell University, I combined my passion for science, healthcare, innovation, and entrepreneurship to explore solutions to some of society's most pressing challenges. My studies allowed me to bridge scientific discovery with real-world application, preparing me to pursue a career dedicated to improving healthcare access and outcomes.
                  </p>

                  {/* Honors */}
                  <div className="bg-secondary rounded-2xl p-8 border border-border">
                    <h4 className="text-sm font-semibold text-foreground tracking-widest uppercase mb-6">Academic Honors & Organizations</h4>
                    <ul className="space-y-3">
                      {honors.map((honor, i) =>
                      <li key={i} className="flex items-start gap-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" />
                          <span className="text-foreground/80 text-sm leading-relaxed">{honor}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </SectionReveal>

              <SectionReveal delay={200}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                    <img
                      src="/assets/images/467583A8-EF7C-4D89-95EF-2E65FB1999D9-1781097506147.JPG"
                      alt="Mary Bellakbira at Cornell University graduation ceremony"
                      className="w-full h-full object-cover" />

                  </div>
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                    <img
                      src="/assets/images/Screenshot_2026-06-10_at_9.42.01_AM-1781098942176.png"
                      alt="Mary Bellakbira at Cornell University graduation with library in background"
                      className="w-full h-full object-cover" />

                  </div>
                </div>
              </SectionReveal>
            </div>
          </div>
        </section>

        {/* ── Research & Discovery ── */}
        <section className="py-28 px-6 bg-secondary/40">
          <div className="max-w-7xl mx-auto">
            <SectionReveal>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-3xl">🧬</span>
                <div>
                  <p className="text-micro text-accent tracking-[0.2em] mb-1">Research & Discovery</p>
                  <h2 className="font-display text-4xl md:text-5xl font-light text-foreground italic">Researching the Future<br /><span className="not-italic font-semibold">of Healthcare</span></h2>
                </div>
              </div>
              <p className="text-foreground/60 max-w-2xl leading-relaxed mb-16 ml-[52px]">
                My passion for research stems from a simple belief: every scientific breakthrough has the potential to improve lives. Throughout my academic career, I have pursued opportunities that allowed me to contribute to research focused on cancer therapeutics, biotechnology, healthcare innovation, and the future of human health.
              </p>
            </SectionReveal>

            {/* Research Institutions — asymmetric bento */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {researchInstitutions.map((inst, i) =>
              <SectionReveal key={inst.id} delay={i * 120} className={i === 0 ? 'md:col-span-2' : ''}>
                  <div className={`bg-background rounded-2xl border border-border overflow-hidden ${i === 0 ? 'grid grid-cols-1 md:grid-cols-2' : 'flex flex-col'}`}>
                    <div className="p-8 flex flex-col justify-center">
                      <h3 className="text-xl font-semibold text-foreground mb-3">{inst.name}</h3>
                      <p className="text-foreground/65 leading-relaxed text-sm">{inst.description}</p>
                    </div>
                    {i === 0 && (inst as {image?: string;}).image &&
                  <div className="p-6">
                        <div className="aspect-[16/9] rounded-2xl overflow-hidden">
                          <img
                        src={(inst as {image?: string;}).image}
                        alt={inst.label}
                        className="w-full h-full object-cover" />

                        </div>
                      </div>
                  }
                  </div>
                </SectionReveal>
              )}
            </div>

            {/* 12+ Presentations */}
            <SectionReveal delay={200}>
              <div className="bg-foreground rounded-2xl p-10 md:p-14 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div>
                  <p className="font-display text-7xl font-semibold text-accent mb-4">12+</p>
                  <h3 className="text-2xl font-semibold text-white mb-4">Research Presentations</h3>
                  <p className="text-white/60 leading-relaxed">
                    Research creates impact when it is shared. I have presented scientific findings, healthcare innovations, and research projects at conferences, symposiums, competitions, and academic events, helping translate complex science into meaningful conversations that inspire action and collaboration.
                  </p>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img src="/assets/images/Screenshot_2026-06-10_at_9.25.39_AM-1781097976356.png" alt="Mary Bellakbira presenting research at an academic conference lecture hall" className="w-full h-full object-cover" />
                </div>
              </div>
            </SectionReveal>
          </div>
        </section>

        {/* ── Innovation & Entrepreneurship ── */}
        <section className="py-28 px-6 bg-background">
          <div className="max-w-7xl mx-auto">
            <SectionReveal>
              <div className="flex items-center gap-4 mb-4">
                <span className="text-3xl">🚀</span>
                <p className="text-micro text-accent tracking-[0.2em]">Innovation & Entrepreneurship</p>
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-light text-foreground italic mb-4 ml-[52px]">
                Education taught me how to ask questions.<br />
                <span className="not-italic font-semibold">Entrepreneurship taught me how to build solutions.</span>
              </h2>
              <p className="text-foreground/60 max-w-2xl leading-relaxed mb-16 ml-[52px]">
                Throughout my journey, I have founded and supported organizations dedicated to solving real-world challenges through innovation, technology, and community impact.
              </p>
            </SectionReveal>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Food for Thought */}
              <SectionReveal delay={100}>
                <div className="bg-secondary rounded-2xl p-8 border border-border h-full">
                  <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center mb-6">
                    <span className="text-2xl">🌱</span>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">Food for Thought Foundation</h3>
                  <p className="text-foreground/65 leading-relaxed mb-6">
                    Founded a nonprofit organization focused on addressing food insecurity through School Health Hubs that combine food access, STEM education, nutrition, wellness, and preventative health.
                  </p>
                  <p className="text-foreground/65 leading-relaxed">
                    Today, our programs help students learn hydroponics, engineering, coding, sustainability, and healthy living while creating healthier communities.
                  </p>
                </div>
              </SectionReveal>

              {/* Healthcare Innovation */}
              <SectionReveal delay={200}>
                <div className="bg-foreground rounded-2xl p-8 h-full">
                  <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mb-6">
                    <span className="text-2xl">⚕️</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">Healthcare Innovation</h3>
                  <p className="text-white/60 leading-relaxed mb-6">
                    Through research, entrepreneurship, and industry experience, I have worked on projects focused on:
                  </p>
                  <ul className="space-y-2 mb-6">
                    {healthcareItems.map((item, i) =>
                    <li key={i} className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                        <span className="text-white/75 text-sm">{item}</span>
                      </li>
                    )}
                  </ul>
                  <p className="text-white/50 text-sm italic leading-relaxed">
                    For me, innovation is not about creating technology for the sake of technology—it is about creating solutions that improve people's lives.
                  </p>
                </div>
              </SectionReveal>
            </div>
          </div>
        </section>

        {/* ── Honors & Recognition ── */}
        <section className="py-28 px-6 bg-foreground">
          <div className="max-w-7xl mx-auto">
            <SectionReveal>
              <div className="flex items-center gap-4 mb-16">
                <span className="text-3xl">🏆</span>
                <div>
                  <p className="text-micro text-accent tracking-[0.2em] mb-1">Honors & Recognition</p>
                  <h2 className="font-display text-4xl md:text-5xl font-light text-white italic">Jerome "Brud" Holland Award</h2>
                </div>
              </div>
            </SectionReveal>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <SectionReveal delay={100}>
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden">
                  <img
                    src="/assets/images/Screenshot_2026-06-10_at_9.38.24_AM-1781098722095.png"
                    alt="Mary Bellakbira in Miss Heart of New York sash holding the Jerome Brud Holland Award at Cornell University ceremony"
                    className="w-full h-full object-cover" />

                </div>
              </SectionReveal>

              <SectionReveal delay={200}>
                <div>
                  <div className="w-16 h-px bg-accent/60 mb-8" />
                  <p className="text-white/75 leading-relaxed mb-6">
                    I was deeply honored to receive Cornell University's Jerome "Brud" Holland Award, recognizing scholarship, leadership, diversity, and community impact.
                  </p>
                  <p className="text-white/75 leading-relaxed mb-6">
                    Named after the first African American football player at Cornell, a U.S. Ambassador, university president, and trailblazer, the award celebrates individuals who create opportunities not only for themselves, but for others.
                  </p>
                  <p className="text-white/75 leading-relaxed mb-6">
                    His story—from shoveling coal to pay for his education to becoming one of the nation's most influential leaders—serves as a reminder that access is powerful, but what we choose to do with that access matters even more.
                  </p>
                  <p className="text-white/75 leading-relaxed mb-10">
                    Receiving this award was especially meaningful because it reflects the values that have shaped my journey: education, service, leadership, and a commitment to expanding opportunity for future generations.
                  </p>
                  <div className="border-l-2 border-accent pl-6">
                    <p className="text-accent/90 italic text-lg font-light leading-relaxed">
                      "To me, scholarship is freedom—the freedom to learn, lead, innovate, and build a future that positively impacts others."
                    </p>
                  </div>
                  <div className="mt-8">
                    <a
                      href="https://news.cornell.edu/stories/2026/05/oadi-celebrates-decade-honors-achievements"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors text-sm font-medium tracking-wide">

                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Read the Cornell OADI Feature
                    </a>
                  </div>
                </div>
              </SectionReveal>
            </div>
          </div>
        </section>

        {/* ── Mentorship & Leadership ── */}
        <section className="py-28 px-6 bg-background">
          <div className="max-w-7xl mx-auto">
            <SectionReveal>
              <div className="flex items-center gap-4 mb-4">
                <span className="text-3xl">🤝</span>
                <p className="text-micro text-accent tracking-[0.2em]">Mentorship & Leadership</p>
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-light text-foreground italic mb-4 ml-[52px]">
                One of the greatest gifts education<br />
                <span className="not-italic font-semibold">has given me is the ability to share it.</span>
              </h2>
            </SectionReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              {/* Kode With Klossy */}
              <SectionReveal delay={100} className="md:col-span-1">
                <div className="bg-secondary rounded-2xl overflow-hidden border border-border h-full flex flex-col">
                  <div className="aspect-[16/9] w-full overflow-hidden">
                    <img src="/assets/images/IMG_8104-1781100207514.jpg" alt="Mary at a Kode With Klossy coding session" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-7 flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-3">Kode With Klossy</h3>
                    <p className="text-foreground/65 text-sm leading-relaxed">
                      As a mentor and advocate for technology education, I have helped students develop technical skills, build applications, and explore careers in STEM.
                    </p>
                  </div>
                </div>
              </SectionReveal>

              {/* Girl Up */}
              <SectionReveal delay={200} className="md:col-span-1">
                <div className="bg-secondary rounded-2xl overflow-hidden border border-border h-full flex flex-col">
                  <div className="aspect-[16/9] w-full overflow-hidden">
                    <img src="/assets/images/IMG_8103-1781100139489.jpg" alt="Mary standing in front of the United Nations Foundation building" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-7 flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-3">Girl Up | United Nations Foundation</h3>
                    <p className="text-foreground/65 text-sm leading-relaxed">
                      As a youth leader, I have worked to empower young women through leadership development, advocacy, and educational opportunities that inspire confidence and create lasting impact.
                    </p>
                  </div>
                </div>
              </SectionReveal>

              {/* STEM Mentorship */}
              <SectionReveal delay={300} className="md:col-span-1">
                <div className="bg-foreground rounded-2xl overflow-hidden h-full flex flex-col">
                  <div className="p-7 flex-1">
                    <h3 className="text-lg font-semibold text-white mb-3">STEM Mentorship & Outreach</h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                      Whether through McNair, Food for Thought, research programs, or community partnerships, I remain committed to helping students discover their potential and pursue opportunities they may have never imagined for themselves.
                    </p>
                    <p className="text-accent/80 text-sm italic mt-4">Because education becomes most powerful when it is shared.</p>
                  </div>
                </div>
              </SectionReveal>
            </div>
          </div>
        </section>

        {/* ── Philosophy ── */}
        <section className="py-28 px-6 bg-secondary/40">
          <div className="max-w-4xl mx-auto text-center">
            <SectionReveal>
              <p className="text-micro text-accent tracking-[0.25em] mb-8">My Philosophy</p>
              <h2 className="font-display text-4xl md:text-5xl font-light text-foreground italic mb-8 leading-tight">
                I believe education is one of<br />
                <span className="not-italic font-semibold">our greatest gifts.</span>
              </h2>
              <div className="w-16 h-px bg-accent/60 mx-auto mb-10" />
              <p className="text-foreground/65 leading-relaxed mb-6 text-lg">It equips us to solve problems, serve our communities, and build a future greater than ourselves. Every classroom, laboratory, mentorship opportunity, and leadership experience has reinforced my belief that knowledge carries a responsibility, to use what we learn in service of others.

              </p>
              <p className="text-foreground/65 leading-relaxed mb-6">
                My goal is not simply to succeed.
              </p>
              <p className="text-foreground/65 leading-relaxed mb-10">
                My goal is to create opportunities, improve lives, and leave every community better than I found it.
              </p>
              <div className="bg-foreground rounded-2xl p-10 md:p-14">
                <p className="text-accent/90 italic text-xl md:text-2xl font-light leading-relaxed">
                  "The true measure of education is not what we achieve for ourselves, but the impact we create for others."
                </p>
              </div>
            </SectionReveal>
          </div>
        </section>

      </main>
      <Footer />
    </>);

}